import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../../components/ui/dialog";
import { AccountingInvoice, useInvoices } from "../../helper/AccountingContext";
import { exportInvoiceExcel } from "../../helper/exportInvoiceHelper";
import { EditableInvoiceFormData } from "./AccountingTable";
import {
  FileTextIcon,
  CalendarIcon,
  Building2Icon,
  UserIcon,
  EuroIcon,
  LandmarkIcon,
  BanknoteIcon,
  Trash2Icon,
  XCircleIcon,
  SearchIcon,
} from "lucide-react";
import { GearIcon } from "@radix-ui/react-icons";

export type InvoicePatchPayload = Partial<
  Omit<AccountingInvoice, "kostenstelle" | "paymentReason" | "financingPlanItem">
> & {
  kostenstelle?: string | number | null;
  paymentReason?: string | number | null;
  financingPlanItem?: string | number | null;
};

interface Kostenstelle {
  id: number;
  name: string;
}
interface PaymentReason {
  id: number;
  reason: string;
  invoices: AccountingInvoice[];
}
interface FinancingPlanItem {
  id: number;
  item: string;
  invoices: AccountingInvoice[];
}
interface InvoiceListProps {
  refreshTrigger?: number;
}
const InfoLine = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number | undefined | null;
}) => (
  <div className="flex items-center gap-2 text-muted-foreground">
    {icon}
    <span>
      <span className="font-medium">{label}:</span> <span className="text-primary">{value ?? "—"}</span>
    </span>
  </div>
);

export const InvoiceList: React.FC<InvoiceListProps> = ({ refreshTrigger }) => {
  const { invoices, fetchInvoices, updateInvoice, deleteInvoice } = useInvoices();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editData, setEditData] = useState<EditableInvoiceFormData>({});
  const [kostenstellen, setKostenstellen] = useState<Kostenstelle[]>([]);
  const [reason, setReason] = useState<PaymentReason[]>([]);
  const [item, setItem] = useState<FinancingPlanItem[]>([]);
  const [sellers, setSellers] = useState<string[]>([]);
  const [exportFromDate, setExportFromDate] = useState<string>("");
  const [exportToDate, setExportToDate] = useState<string>("");
  const [showExportForm, setShowExportForm] = useState(false);
  const [exportKostenstelle, setExportKostenstelle] = useState<string>("Alle");

  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [searchResults, setSearchResults] = useState<AccountingInvoice[] | null>(null);
  const [filter, setFilter] = useState({
    kostenstelle: "Alle",
    paymentReason: "Alle",
    financingPlanItem: "Alle",
    fromDate: "",
    toDate: "",
    minGross: "",
    maxGross: "",
  });
  const [loading, setLoading] = useState(false);

  // Auswahlfelder laden
  useEffect(() => {
    fetchInvoices();
  }, [refreshTrigger]);
  useEffect(() => {
    const fetchKostenstellen = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get<Kostenstelle[]>("/api/accounting/kostenstellen", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setKostenstellen(response.data);
      } catch (error) {
        console.error("Fehler beim Abrufen der Kostenstellen:", error);
      }
    };
    const fetchReason = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get<PaymentReason[]>("/api/accounting/payment-reasons", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setReason(response.data);
      } catch (error) {
        console.error("Fehler beim Abrufen der Zahlungsgründe:", error);
      }
    };
    const fetchItem = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get<FinancingPlanItem[]>("/api/accounting/financing-plan-items", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setItem(response.data);
      } catch (error) {
        console.error("Fehler beim Abrufen der Finanzierungspositionen:", error);
      }
    };
    fetchKostenstellen();
    fetchReason();
    fetchItem();
  }, []);
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const fetchData = async () => {
      try {
        const [sellerRes] = await Promise.all([
          axios.get("/api/accounting/sellers", { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setSellers(sellerRes.data);
      } catch (err) {
        console.error("Fehler beim Laden der Verkäufer:", err);
      }
    };
    fetchData();
  }, []);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setSearchResults(null);
      setActiveSearch("");
      return;
    }
    setLoading(true);
    setActiveSearch(searchTerm); // <--- Nur hier wird der "aktive Suchmodus" gesetzt
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get("/api/accounting/invoices/search", {
        params: { term: searchTerm.trim() },
        headers: { Authorization: `Bearer ${token}` },
      });
      setSearchResults(response.data);
    } catch {
      setSearchResults([]);
    }
    setLoading(false);
  };

  const handleFilterChange = (changed: Partial<typeof filter>) => {
    setFilter((prev) => ({ ...prev, ...changed }));
  };
  const fetchFilteredInvoices = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const params: any = {};
      if (filter.kostenstelle && filter.kostenstelle !== "Alle") params.kostenstelle = filter.kostenstelle;
      if (filter.paymentReason && filter.paymentReason !== "Alle") params.paymentReason = filter.paymentReason;
      if (filter.financingPlanItem && filter.financingPlanItem !== "Alle")
        params.financingPlanItem = filter.financingPlanItem;
      if (filter.fromDate) params.fromDate = filter.fromDate;
      if (filter.toDate) params.toDate = filter.toDate;
      if (filter.minGross) params.minGross = filter.minGross;
      if (filter.maxGross) params.maxGross = filter.maxGross;

      const response = await axios.get("/api/accounting/invoices", {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setSearchResults(response.data);
    } catch {
      setSearchResults([]);
    }
    setLoading(false);
  };

  function useDebouncedEffect(effect: () => void, deps: any[], delay: number) {
    const callback = useRef(effect);

    useEffect(() => {
      callback.current = effect;
    }, [effect]);

    useEffect(() => {
      if (delay === null) return;
      const handler = setTimeout(() => callback.current(), delay);
      return () => clearTimeout(handler);
    }, [...deps, delay]);
  }

  useDebouncedEffect(
    () => {
      if (searchTerm) handleSearch();
    },
    [searchTerm],
    900
  );

  const isFilterActive = !!Object.values(filter).find((val) => val && val !== "Alle");
  const toDisplay = activeSearch
    ? searchResults ?? []
    : searchResults !== null && isFilterActive
    ? searchResults
    : invoices;
  return (
    <div className="flex flex-col gap-6 mt-12">
      <div className="flex flex-col gap-2">
        <div className="flex flex-col sm:flex-row flex-wrap gap-4 w-full items-end">
          {/* Suchleiste */}
          <div className="flex items-center justify-center gap-4">
            <div className="flex relative">
              <Input
                type="search"
                placeholder="Rechnungsnummer, Name, IBAN..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="w-full sm:w-[480px] p-6 text-xl placeholder:text-xl"
                disabled={loading}
              />
              {searchTerm && (
                <Button
                  onClick={() => {
                    setSearchTerm("");
                    setSearchResults(null);
                    setActiveSearch("");
                  }}
                  className="bg-transparent hover:bg-transparent shadow-none border-none absolute right-0 top-1/2 -translate-y-1/2"
                  disabled={!searchTerm && !activeSearch}>
                  <XCircleIcon className="text-neutral-500 w-6 h-6" />
                </Button>
              )}
            </div>
            <Button
              onClick={handleSearch}
              className="flex-grow-0 p-6 flex items-center gap-2"
              disabled={loading || !searchTerm}>
              <SearchIcon />
              Suchen
            </Button>
          </div>
          {/* Excel-Export */}
          <Dialog open={showExportForm} onOpenChange={setShowExportForm}>
            <DialogTrigger asChild>
              <Button className="ml-auto p-6">Excel-Export starten</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Zeitraum wählen</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col items-start justify-start gap-2">
                <Label>Kostenstelle</Label>
                <Select value={exportKostenstelle} onValueChange={setExportKostenstelle}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Kostenstelle" />
                  </SelectTrigger>
                  <SelectContent className="w-full">
                    <SelectItem value="Alle">Alle</SelectItem>
                    {kostenstellen.map((ks) => (
                      <SelectItem key={ks.id} value={ks.name}>
                        {ks.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <Label>Von</Label>
                  <Input type="date" value={exportFromDate} onChange={(e) => setExportFromDate(e.target.value)} />
                </div>
                <div className="flex flex-col gap-1">
                  <Label>Bis</Label>
                  <Input type="date" value={exportToDate} onChange={(e) => setExportToDate(e.target.value)} />
                </div>
                <Button
                  onClick={() => {
                    if (!exportFromDate || !exportToDate) {
                      alert("Bitte einen vollständigen Zeitraum eingeben.");
                      return;
                    }
                    exportInvoiceExcel(exportKostenstelle, exportFromDate, exportToDate);
                    setShowExportForm(false);
                  }}>
                  Exportieren
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        {/* {/* Modus-Anzeige
        <div className="h-6">
          {loading && <div className="text-center py-2">Lade...</div>}
          {!loading && searchTerm && (
            <div className="text-muted-foreground text-xs">Suchergebnisse für „{searchTerm}“</div>
          )}
          {!loading && isFilterActive && !searchTerm && (
            <div className="text-muted-foreground text-xs">Filter aktiv</div>
          )}
          {!loading && !searchTerm && !isFilterActive && (
            <div className="text-muted-foreground text-xs">Alle Rechnungen</div>
          )}
          {!loading && toDisplay.length === 0 && (
            <div className="text-center text-muted-foreground py-8">Keine Rechnungen gefunden.</div>
          )}
        </div> */}
      </div>
      {/* Rechnungsanzeige */}
      <div className="w-full border-t pt-4 space-y-4">
        {toDisplay.map((invoice) => (
          <div
            key={invoice.id}
            className="p-6 border rounded-2xl bg-secondary dark:bg-neutral-700 shadow-md flex flex-col gap-6">
            {/* Header: Verkäufer & Kostenstelle */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 pb-2 border-b border-muted">
              <div className="flex items-center gap-2">
                <Building2Icon className="w-6 h-6 text-primary" />
                <span className="text-2xl font-bold">{invoice.sellerName}</span>
              </div>
              <div className="flex items-center gap-2">
                <LandmarkIcon className="w-5 h-5 text-accent" />
                <span className="text-lg font-medium text-muted-foreground">
                  Kostenstelle:
                  <span className="ml-2 font-semibold text-primary">
                    {typeof invoice.kostenstelle === "object"
                      ? invoice.kostenstelle?.name
                      : invoice.kostenstelle || "—"}
                  </span>
                </span>
              </div>
            </div>
            {/* Zusatzinfos */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
              <InfoLine
                icon={<FileTextIcon className="w-4 h-4" />}
                label="Rechnungsnummer"
                value={invoice.invoiceNumber}
              />
              <InfoLine
                icon={<CalendarIcon className="w-4 h-4" />}
                label="Datum"
                value={invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString() : "—"}
              />
              <InfoLine icon={<UserIcon className="w-4 h-4" />} label="Besteller" value={invoice.buyerName} />
              <InfoLine
                icon={<EuroIcon className="w-4 h-4" />}
                label="Brutto"
                value={Number(invoice.grossAmount).toFixed(2) + " €"}
              />
              <InfoLine
                icon={<EuroIcon className="w-4 h-4" />}
                label="Netto"
                value={Number(invoice.netAmount).toFixed(2) + " €"}
              />
              <InfoLine
                icon={<LandmarkIcon className="w-4 h-4" />}
                label="Finanzierungsposition"
                value={
                  typeof invoice.financingPlanItem === "object"
                    ? invoice.financingPlanItem?.item
                    : invoice.financingPlanItem || "—"
                }
              />
              <InfoLine
                icon={<BanknoteIcon className="w-4 h-4" />}
                label="Zahlungsgrund"
                value={
                  typeof invoice.paymentReason === "object"
                    ? invoice.paymentReason?.reason
                    : invoice.paymentReason || "—"
                }
              />
              <InfoLine
                icon={<BanknoteIcon className="w-4 h-4" />}
                label="Zahlungsmethode"
                value={invoice.paymentMethod || "—"}
              />
              <InfoLine icon={<BanknoteIcon className="w-4 h-4" />} label="IBAN" value={invoice.iban || "—"} />
              <InfoLine
                icon={<CalendarIcon className="w-4 h-4" />}
                label="Fällig am"
                value={invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : "—"}
              />
              <InfoLine
                icon={<CalendarIcon className="w-4 h-4" />}
                label="Bestelldatum"
                value={invoice.orderDate ? new Date(invoice.orderDate).toLocaleDateString() : "—"}
              />
            </div>
            {/* Aktionen */}
            <div className="flex items-center justify-end w-full gap-4">
              <Button
                size="lg"
                className="flex items-center justify-center gap-4"
                variant="outline"
                onClick={() => {
                  setEditingId(invoice.id);
                  setEditData({
                    ...invoice,
                    invoiceDate: invoice.invoiceDate ? new Date(invoice.invoiceDate).toISOString().split("T")[0] : "",
                    dueDate: invoice.dueDate ? new Date(invoice.dueDate).toISOString().split("T")[0] : "",
                    orderDate: invoice.orderDate ? new Date(invoice.orderDate).toISOString().split("T")[0] : "",
                    paymentDate: invoice.paymentDate ? new Date(invoice.paymentDate).toISOString().split("T")[0] : "",
                    kostenstelle: typeof invoice.kostenstelle === "object" ? invoice.kostenstelle?.name : "",
                    paymentReason: typeof invoice.paymentReason === "object" ? invoice.paymentReason?.reason : "",
                    financingPlanItem:
                      typeof invoice.financingPlanItem === "object" ? invoice.financingPlanItem?.item : "",
                  });
                }}>
                <GearIcon />
                Bearbeiten
              </Button>
              <Button
                variant="destructive"
                size="lg"
                className="flex items-center justify-center gap-4 "
                onClick={async () => {
                  if (window.confirm("Rechnung wirklich löschen?")) {
                    await deleteInvoice(invoice.id);
                  }
                }}
                title="Rechnung löschen">
                <Trash2Icon className="w-4 h-4" />
                <p>Rechnung löschen</p>
              </Button>
            </div>
          </div>
        ))}
      </div>
      {/* Dialog zum Bearbeiten */}
      <Dialog open={!!editingId} onOpenChange={(open) => !open && setEditingId(null)}>
        <DialogContent className="max-w-[75vw]">
          <DialogHeader>
            <DialogTitle>Rechnung bearbeiten</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {/* Rechnungsnummer */}
            <div>
              <Label>Rechnungsnummer</Label>
              <Input
                value={editData.invoiceNumber || ""}
                onChange={(e) => setEditData({ ...editData, invoiceNumber: e.target.value })}
              />
            </div>
            {/* Rechnungsdatum */}
            <div>
              <Label>Rechnungsdatum</Label>
              <Input
                type="date"
                value={
                  typeof editData.invoiceDate === "string"
                    ? editData.invoiceDate
                    : editData.invoiceDate
                    ? new Date(editData.invoiceDate).toISOString().split("T")[0]
                    : ""
                }
                onChange={(e) => setEditData({ ...editData, invoiceDate: e.target.value })}
              />
            </div>
            {/* Verkäufer */}
            <div>
              <Label>Verkäufer</Label>
              <Select
                value={editData.sellerName}
                onValueChange={(value) => setEditData({ ...editData, sellerName: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {sellers.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder="Name"
                value={editData.sellerName || ""}
                onChange={(e) => setEditData({ ...editData, sellerName: e.target.value })}
              />
            </div>
            {/* Verkäufer Straße */}
            <div>
              <Label>Verkäufer Straße</Label>
              <Input
                placeholder="Straße"
                value={editData.sellerStreet || ""}
                onChange={(e) => setEditData({ ...editData, sellerStreet: e.target.value })}
              />
            </div>
            {/* Verkäufer PLZ */}
            <div>
              <Label>Verkäufer PLZ</Label>
              <Input
                placeholder="PLZ"
                value={editData.sellerPostalCode || ""}
                onChange={(e) => setEditData({ ...editData, sellerPostalCode: e.target.value })}
              />
            </div>
            {/* Verkäufer Ort */}
            <div>
              <Label>Verkäufer Ort</Label>
              <Input
                placeholder="Ort"
                value={editData.sellerCity || ""}
                onChange={(e) => setEditData({ ...editData, sellerCity: e.target.value })}
              />
            </div>
            {/* Käufer */}
            <div>
              <Label>Käufer</Label>
              <Input
                placeholder="Name"
                value={editData.buyerName || ""}
                onChange={(e) => setEditData({ ...editData, buyerName: e.target.value })}
              />
            </div>
            {/* Käufer Straße */}
            <div>
              <Label>Käufer Straße</Label>
              <Input
                placeholder="Straße"
                value={editData.buyerStreet || ""}
                onChange={(e) => setEditData({ ...editData, buyerStreet: e.target.value })}
              />
            </div>
            {/* Käufer PLZ */}
            <div>
              <Label>Käufer PLZ</Label>
              <Input
                placeholder="PLZ"
                value={editData.buyerPostalCode || ""}
                onChange={(e) => setEditData({ ...editData, buyerPostalCode: e.target.value })}
              />
            </div>
            {/* Käufer Ort */}
            <div>
              <Label>Käufer Ort</Label>
              <Input
                placeholder="Ort"
                value={editData.buyerCity || ""}
                onChange={(e) => setEditData({ ...editData, buyerCity: e.target.value })}
              />
            </div>
            {/* Brutto/Netto/USt */}
            <div>
              <Label>Bruttobetrag</Label>
              <Input
                type="number"
                max={99999999.99}
                step={0.01}
                value={editData.grossAmount || ""}
                onChange={(e) => {
                  let value = parseFloat(e.target.value);
                  if (value > 99999999.99) value = 99999999.99;
                  setEditData({ ...editData, grossAmount: parseFloat(e.target.value) });
                }}
              />
            </div>
            <div>
              <Label>Nettobetrag</Label>
              <Input
                type="number"
                max={99999999.99}
                step={0.01}
                value={editData.netAmount || ""}
                onChange={(e) => {
                  let value = parseFloat(e.target.value);
                  if (value > 99999999.99) value = 99999999.99;
                  setEditData({ ...editData, netAmount: parseFloat(e.target.value) });
                }}
              />
            </div>
            <div>
              <Label>USt. Betrag</Label>
              <Input
                type="number"
                value={editData.vatAmount || ""}
                onChange={(e) => setEditData({ ...editData, vatAmount: parseFloat(e.target.value) })}
              />
            </div>
            <div>
              <Label>USt. Satz (%)</Label>
              <Input
                type="number"
                value={editData.vatRate || ""}
                onChange={(e) => setEditData({ ...editData, vatRate: parseFloat(e.target.value) })}
              />
            </div>
            {/* Währung */}
            <div>
              <Label>Währung</Label>
              <Input
                placeholder="z. B. EUR"
                value={editData.currencyCode || ""}
                onChange={(e) => setEditData({ ...editData, currencyCode: e.target.value })}
              />
            </div>
            {/* IBAN/BIC */}
            <div>
              <Label>IBAN</Label>
              <Input value={editData.iban || ""} onChange={(e) => setEditData({ ...editData, iban: e.target.value })} />
            </div>
            <div>
              <Label>BIC</Label>
              <Input value={editData.bic || ""} onChange={(e) => setEditData({ ...editData, bic: e.target.value })} />
            </div>
            {/* Zahlungsmethode & -referenz */}
            <div>
              <Label>Zahlungsmethode</Label>
              <Input
                value={editData.paymentMethod || ""}
                onChange={(e) => setEditData({ ...editData, paymentMethod: e.target.value })}
              />
            </div>
            <div>
              <Label>Zahlungsreferenz</Label>
              <Input
                value={editData.paymentReference || ""}
                onChange={(e) => setEditData({ ...editData, paymentReference: e.target.value })}
              />
            </div>
            {/* Fälligkeit/Bestelldatum/Zahlungsdatum */}
            <div>
              <Label>Fällig am</Label>
              <Input
                type="date"
                value={
                  typeof editData.dueDate === "string"
                    ? editData.dueDate
                    : editData.dueDate
                    ? new Date(editData.dueDate).toISOString().split("T")[0]
                    : ""
                }
                onChange={(e) => setEditData({ ...editData, dueDate: e.target.value })}
              />
            </div>
            <div>
              <Label>Bestelldatum</Label>
              <Input
                type="date"
                value={
                  typeof editData.orderDate === "string"
                    ? editData.orderDate
                    : editData.orderDate
                    ? new Date(editData.orderDate).toISOString().split("T")[0]
                    : ""
                }
                onChange={(e) => setEditData({ ...editData, orderDate: e.target.value })}
              />
            </div>
            <div>
              <Label>Zahlungsdatum</Label>
              <Input
                type="date"
                value={
                  typeof editData.paymentDate === "string"
                    ? editData.paymentDate
                    : editData.paymentDate
                    ? new Date(editData.paymentDate).toISOString().split("T")[0]
                    : ""
                }
                onChange={(e) => setEditData({ ...editData, paymentDate: e.target.value })}
              />
            </div>
            {/* Kostenstelle */}
            <div>
              <Label>Kostenstelle</Label>
              <Select
                value={kostenstellen.find((k) => k.name === editData.kostenstelle) ? editData.kostenstelle : ""}
                onValueChange={(value) => setEditData({ ...editData, kostenstelle: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Kostenstelle wählen" />
                </SelectTrigger>
                <SelectContent>
                  {kostenstellen.map((k) => (
                    <SelectItem key={k.id} value={k.name}>
                      {k.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className="mt-2"
                placeholder="Oder neue eingeben"
                value={editData.kostenstelle || ""}
                onChange={(e) => setEditData({ ...editData, kostenstelle: e.target.value })}
              />
            </div>
            {/* Zahlungsgrund */}
            <div>
              <Label>Zahlungsgrund</Label>
              <Select
                value={typeof editData.paymentReason === "string" ? "" : editData.paymentReason || ""}
                onValueChange={(value) => setEditData({ ...editData, paymentReason: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Zahlungsgrund wählen" />
                </SelectTrigger>
                <SelectContent>
                  {reason.map((r) => (
                    <SelectItem key={r.id} value={r.reason}>
                      {r.reason}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className="mt-2"
                placeholder="Oder neuen eingeben"
                value={editData.paymentReason || ""}
                onChange={(e) => setEditData({ ...editData, paymentReason: e.target.value })}
              />
            </div>
            {/* Finanzierungsposition */}
            <div>
              <Label>Finanzierungsposition</Label>
              <Select
                value={typeof editData.financingPlanItem === "string" ? "" : editData.financingPlanItem || ""}
                onValueChange={(value) => setEditData({ ...editData, financingPlanItem: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Finanzierungsposition wählen" />
                </SelectTrigger>
                <SelectContent>
                  {item.map((i) => (
                    <SelectItem key={i.id} value={i.item}>
                      {i.item}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className="mt-2"
                placeholder="Oder neue eingeben"
                value={editData.financingPlanItem || ""}
                onChange={(e) => setEditData({ ...editData, financingPlanItem: e.target.value })}
              />
            </div>
          </div>
          <Button
            className="w-full mt-6"
            onClick={async () => {
              if (
                !editData.kostenstelle ||
                !editData.paymentReason ||
                !editData.financingPlanItem ||
                !editData.orderDate
              ) {
                alert("Bitte alle Pflichtfelder ausfüllen.");
                return;
              }
              const patchPayload: InvoicePatchPayload = {
                ...editData,
                invoiceDate: editData.invoiceDate ? new Date(editData.invoiceDate) : undefined,
                dueDate: editData.dueDate ? new Date(editData.dueDate) : undefined,
                orderDate: editData.orderDate ? new Date(editData.orderDate) : undefined,
                paymentDate: editData.paymentDate ? new Date(editData.paymentDate) : undefined,
                kostenstelle: editData.kostenstelle ? editData.kostenstelle : undefined,
                paymentReason: editData.paymentReason ? editData.paymentReason : undefined,
                financingPlanItem: editData.financingPlanItem ? editData.financingPlanItem : undefined,
              };
              try {
                await updateInvoice(editingId!, patchPayload);
                setEditingId(null);
                fetchInvoices();
              } catch (error: any) {
                alert(error?.response?.data?.message || "Fehler beim Speichern!");
              }
            }}>
            Änderungen speichern
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
